import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Construction } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useSiteGate, useUpdateSiteGate } from "@/hooks/useSiteGate";
import type { SiteGateUpdate } from "@/types/siteGate";

export default function SiteGate() {
  const { data: gate, isLoading } = useSiteGate();
  const updateMutation = useUpdateSiteGate();

  const [form, setForm] = useState<SiteGateUpdate>({
    is_enabled: false,
    page_title: "",
    body_text: "",
    login_enabled: true,
    login_hidden: false,
  });

  useEffect(() => {
    if (gate) {
      setForm({
        is_enabled: gate.is_enabled,
        page_title: gate.page_title,
        body_text: gate.body_text,
        login_enabled: gate.login_enabled,
        login_hidden: gate.login_hidden,
      });
    }
  }, [gate]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(form);
      toast.success("Site gate settings saved");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save settings",
      );
    }
  };

  const isDirty =
    gate &&
    (form.is_enabled !== gate.is_enabled ||
      form.page_title !== gate.page_title ||
      form.body_text !== gate.body_text ||
      form.login_enabled !== gate.login_enabled ||
      form.login_hidden !== gate.login_hidden);

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <Toaster />

      <div className="flex items-center gap-3">
        <Construction className="size-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold">Site Gate</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Control access to nvg-nexus-ui with a construction / coming soon
            page.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Enable/Disable */}
          <Card
            className={
              form.is_enabled
                ? "border-yellow-500/50 bg-yellow-500/5"
                : undefined
            }
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Gate Enabled</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    When enabled, only admins and beta testers can access the
                    site.
                  </p>
                </div>
                <Switch
                  checked={form.is_enabled ?? false}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, is_enabled: checked }))
                  }
                  aria-label="Enable site gate"
                />
              </div>
            </CardHeader>
          </Card>

          {/* Page Content */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Page Content</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                What users see when the gate is active.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="page-title">Page Title</Label>
                <Input
                  id="page-title"
                  value={form.page_title ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, page_title: e.target.value }))
                  }
                  placeholder="Coming Soon"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="body-text">Body Text</Label>
                <Textarea
                  id="body-text"
                  value={form.body_text ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, body_text: e.target.value }))
                  }
                  placeholder="We're working on something new. Stay tuned!"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Markdown is supported.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Login Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Login Options</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Control how the login form behaves on the gate page.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="login-enabled">Login Enabled</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow users to attempt to log in from the gate page.
                  </p>
                </div>
                <Switch
                  id="login-enabled"
                  checked={form.login_enabled ?? true}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, login_enabled: checked }))
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="login-hidden">Hide Login (Easter Egg)</Label>
                  <p className="text-sm text-muted-foreground">
                    Require 5 clicks on the page title to reveal the login form.
                  </p>
                </div>
                <Switch
                  id="login-hidden"
                  checked={form.login_hidden ?? false}
                  disabled={!form.login_enabled}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, login_hidden: checked }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Save */}
          <div className="flex justify-end gap-3">
            {isDirty && (
              <Button
                variant="outline"
                onClick={() => {
                  if (gate) {
                    setForm({
                      is_enabled: gate.is_enabled,
                      page_title: gate.page_title,
                      body_text: gate.body_text,
                      login_enabled: gate.login_enabled,
                      login_hidden: gate.login_hidden,
                    });
                  }
                }}
              >
                Discard
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>

          {gate && (
            <p className="text-xs text-muted-foreground text-right">
              Last updated: {new Date(gate.updated_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
